import { Link } from 'react-router-dom';
import { CalendarHeart, Users } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { MemberPortalMembership, MemberPortalMembershipSeat } from '@/services/publicClubMembership';

export const MEMBERSHIP_STATUS_LABELS: Record<MemberPortalMembership['status'], string> = {
  pending_payment: 'Payment pending',
  pending_verification: 'Pending verification',
  active: 'Active',
  past_due: 'Past due',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const MEMBERSHIP_STATUS_BADGE_CLASSES: Record<MemberPortalMembership['status'], string> = {
  pending_payment: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  pending_verification: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  active: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  past_due: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  expired: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

function formatMemberSince(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function membershipPriority(membership: MemberPortalMembership): number {
  if (
    membership.status === 'active' ||
    membership.status === 'pending_payment' ||
    membership.status === 'pending_verification' ||
    membership.status === 'past_due'
  ) {
    return 0;
  }
  return 1;
}

export function deriveMemberSince(memberships: MemberPortalMembership[]): string | null {
  const qualifying = memberships.filter(
    (membership) =>
      membership.status !== 'cancelled' &&
      (membership.paymentStatus === 'paid' || membership.status === 'pending_payment')
  );
  const source = qualifying.length > 0 ? qualifying : memberships;
  const timestamps = source
    .map((membership) => new Date(membership.startDate).getTime())
    .filter((time) => !Number.isNaN(time));

  if (timestamps.length === 0) return null;
  return new Date(Math.min(...timestamps)).toISOString();
}

export function derivePrimaryMembership(
  memberships: MemberPortalMembership[]
): MemberPortalMembership | null {
  return (
    memberships
      .slice()
      .sort((a, b) => membershipPriority(a) - membershipPriority(b))[0] ?? null
  );
}

export function deriveHouseholdSeats(memberships: MemberPortalMembership[]): MemberPortalMembershipSeat[] {
  const primaryMembership = derivePrimaryMembership(memberships);
  if (!primaryMembership || primaryMembership.seats.length <= 1) {
    return [];
  }

  return primaryMembership.seats
    .slice()
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}

function formatSeatType(seatType: MemberPortalMembershipSeat['seatType']): string {
  return seatType === 'junior' ? 'Junior' : 'Adult';
}

export function MembershipMemberSinceCard({
  memberships,
  viewHref,
}: {
  memberships: MemberPortalMembership[];
  viewHref?: string | null;
}) {
  const memberSince = deriveMemberSince(memberships);
  if (!memberSince) return null;

  return (
    <section aria-labelledby="membership-member-since-heading">
      <h2 id="membership-member-since-heading" className="sr-only">
        Membership tenure
      </h2>
      <Card className="overflow-hidden border-amber-200/80 bg-gradient-to-br from-amber-50 to-amber-100/60 shadow-sm dark:border-amber-800/50 dark:from-amber-950/40 dark:to-amber-900/20">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-200/80 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200">
            <CalendarHeart className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-amber-800/70 dark:text-amber-200/80">
              Member since
            </p>
            <p className="text-sm font-semibold text-amber-950 dark:text-amber-50">
              {formatMemberSince(memberSince)}
            </p>
          </div>
          {viewHref ? (
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-auto shrink-0 px-2 py-1 text-xs text-amber-900 hover:bg-amber-200/60 hover:text-amber-950 dark:text-amber-100 dark:hover:bg-amber-900/40 dark:hover:text-amber-50"
            >
              <Link to={viewHref}>View details</Link>
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

export function MembershipHouseholdSection({
  memberships,
}: {
  memberships: MemberPortalMembership[];
}) {
  const householdSeats = deriveHouseholdSeats(memberships);
  if (householdSeats.length === 0) return null;

  return (
    <section aria-labelledby="membership-household-heading">
      <h2
        id="membership-household-heading"
        className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white"
      >
        <Users className="h-5 w-5 text-muted-foreground" aria-hidden />
        Your household
      </h2>
      <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
        <CardContent className="space-y-3 p-4">
          {householdSeats.map((seat) => (
            <div
              key={seat.memberId}
              className="flex items-start justify-between gap-3 border-b border-border pb-3 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 dark:text-white">
                  {seat.firstName} {seat.lastName}
                </p>
                <p className="text-sm text-muted-foreground">{formatSeatType(seat.seatType)}</p>
              </div>
              {seat.isPrimary ? (
                <Badge variant="secondary" className="shrink-0">
                  Primary
                </Badge>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

export function MembershipPlanSummaryCard({
  membership,
  viewHref,
}: {
  membership: MemberPortalMembership;
  viewHref?: string | null;
}) {
  return (
    <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
      <CardContent className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {membership.membershipType?.name ?? 'Membership'}
            </h3>
            {membership.seasonLabel ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Season {membership.seasonLabel}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                MEMBERSHIP_STATUS_BADGE_CLASSES[membership.status]
              )}
            >
              {MEMBERSHIP_STATUS_LABELS[membership.status]}
            </span>
            {viewHref ? (
              <Button asChild variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs">
                <Link to={viewHref}>View</Link>
              </Button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Started</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatDate(String(membership.startDate))}
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">
              {membership.termType === 'recurring' ? 'Renews' : 'Expires'}
            </p>
            <p className="font-medium text-gray-900 dark:text-white">
              {membership.endDate ? formatDate(String(membership.endDate)) : 'Ongoing'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
